import { createTheme } from "@mui/material";

export const baseTheme = createTheme({
  palette: {
    primary: {
      darker: "#35c379",
      dark: "#35c379",
      main: "#41e58f",
      light: "#84f5ba",
      lighter: "#cdfde4",
    },
    secondary: {
      darker: "#1d1dca",
      dark: "#0000f5",
      main: "#5b89ff",
      light: "#b7d4ff",
      lighter: "#e6f0ff",
    },
    tertiary: {
      darker: "#222222",
      dark: "#666666",
      main: "#999999",
      light: "#cccfd8",
      lighter: "#ffffff",
    },
    divider: "#cccfd8",
  },
  typography: (palette) => ({
    fontFamily: "'Inter', serif",
    fontSize: 14,
    h1: {
      fontSize: "1.7rem",
      fontWeight: 600,
      lineHeight: "2.5rem",
      color: palette.tertiary.darker,
    },
    h2: {
      fontSize: "1.2rem",
      fontWeight: 500,
      lineHeight: "2.2rem",
      color: palette.tertiary.darker,
    },
    h3: {
      fontSize: "1.1rem",
      fontWeight: 500,
      lineHeight: "2.1rem",
      color: palette.tertiary.darker,
    },
    h4: {
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: "2rem",
      color: palette.tertiary.darker,
    },
    subtitle1: {
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: "1.5rem",
      color: palette.tertiary.dark,
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: "bold",
      lineHeight: "1.25rem",
      color: palette.tertiary.darker,
    },
    body1: {
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: "1.5rem",
      color: palette.tertiary.darker,
    },
    body2: {
      fontSize: "0.875rem",
      fontWeight: 400,
      lineHeight: "1.25rem",
      color: palette.tertiary.main,
    },
    button: {
      fontSize: "1rem",
      fontWeight: 700,
      lineHeight: "1.5rem",
    },
    link: {
      fontFamily: "'Inter', serif",
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: "1.5rem",
      textDecoration: "none",
      color: palette.secondary.dark,
      cursor: "pointer",

      //style visited links
      "&:visited": {
        color: palette.secondary.dark,
        textDecoration: "none",
      },
      //style hover links
      "&:hover": {
        color: palette.secondary.dark,
        textDecoration: "none",
      },
      //style active links
      "&:active": {
        color: palette.secondary.dark,
        textDecoration: "none",
      },
      //style focus links
      "&:focus": {
        color: palette.secondary.dark,
        textDecoration: "none",
      },
    },
    table: {
      fontFamily: "'Inter', serif",
      fontSize: ".875rem",
      fontWeight: 300,
      lineHeight: "1.2rem",
      color: palette.tertiary.main,
    },
    tableLink: {
      fontFamily: "'Inter', serif",
      fontSize: ".875rem",
      fontWeight: 400,
      lineHeight: "1.2rem",
      color: palette.secondary.darker,
    },
  }),
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1439, //This is the only value we are changing from the defaults
      xl: 1536,
    },
  },
});

export const globalDkMUITheme = createTheme(
  {
    components: {
      MuiDialog: {
        styleOverrides: {
          paper: {
            padding: "10px",
            minWidth: "800px",
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            "& > *": {
              // style children elements of the DialogActions
              margin: "10px",
            },
          },
        },
      },
      MuiDialogTitle: {
        defaultProps: {
          fontSize: "1.5rem",
          fontWeight: baseTheme.typography.h1.fontWeight,
          lineHeight: baseTheme.typography.h1.lineHeight,
          color: baseTheme.typography.h1.color,
        },
      },
      MuiDialogContentText: {
        defaultProps: {
          fontSize: baseTheme.typography.subtitle1.fontSize,
          color: baseTheme.palette.tertiary.darker,
        },
      },
    },
  },
  baseTheme
);

declare module "@mui/material/styles" {
  interface Palette {
    tertiary: Palette["primary"];
  }
  interface PaletteOptions {
    tertiary: PaletteOptions["primary"];
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
}

// update the theme typography typings
declare module "@mui/material/styles" {
  interface TypographyVariants {
    link: React.CSSProperties;
    table: React.CSSProperties;
    tableLink: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    link?: React.CSSProperties;
    table?: React.CSSProperties;
    tableLink?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    link: true;
    table: true;
    tableLink: true;
  }
}
